<script setup>
import { computed, ref, watch } from 'vue';

import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import KraftchatAIAPI from 'dashboard/api/kraftchatAI';

import {
  formatProviderName,
  formatChannelType,
} from '../../helpers/kraftchatAIFormatters';
import {
  CUSTOM_TONE_VALUE,
  TONE_PRESETS,
  resolveTonePresetValue,
  SPEECH_MODEL_OPTIONS,
  SPEECH_PROFILE_OPTIONS,
  NEW_AGENT_SPEECH_MODEL,
  NEW_AGENT_SPEECH_PROFILE,
  AUTOMATIC_MODEL_HINT,
  SPEECH_MODELS_WITHOUT_PROFILE_SUPPORT,
} from '../../helpers/kraftchatAIAgentFormConfig';

const props = defineProps({
  agent: {
    type: Object,
    default: () => null,
  },
  providerCredentials: {
    type: Array,
    default: () => [],
  },
  // The single inbox this form is currently scoped to, chosen beforehand in
  // KraftchatAIInboxList - {id, name, channel_type, ai_allowed,
  // blocked_reason}. This form never lets an admin switch an existing
  // agent to a different inbox; picking another inbox in the list starts
  // configuring that inbox's own agent instead (see Etapa 7 of the plan:
  // the agent/inbox link is treated as stable).
  inbox: {
    type: Object,
    default: () => null,
  },
});

const emit = defineEmits(['saved']);

const agentFormContent = {
  title: 'Configurar agente IA',
  description:
    'Defina como o Kraftchat deve atender clientes automaticamente no canal conectado.',
  enabledLabel: 'Ativar atendimento com IA',
  enabledDescription:
    'Quando ativo, o agente poderá responder conversas usando o provedor conectado.',
  enabledActiveLabel: 'IA ativa',
  enabledInactiveLabel: 'IA inativa',
  pendingChangeLabel: 'Alteração pendente. Salve para aplicar.',
  nameLabel: 'Nome do agente',
  inboxLabel: 'Caixa de entrada',
  inboxSummaryLabel: 'Caixa de entrada',
  channelSummaryLabel: 'Canal',
  providerLabel: 'Provedor conectado',
  identitySectionTitle: 'Identidade e comportamento',
  toneLabel: 'Tom de atendimento',
  toneCustomOptionLabel: 'Personalizado',
  instructionsLabel: 'Instruções da IA',
  greetingMessageLabel: 'Mensagem de saudação',
  advancedSectionTitle: 'Configurações avançadas',
  temperatureLabel: 'Criatividade',
  temperatureHelp:
    'Controla o quanto as respostas podem variar. Valores menores deixam o agente mais previsível.',
  maxTokensLabel: 'Máximo de tokens',
  maxTokensHelp: 'Limite técnico do tamanho máximo gerado pelo modelo.',
  voiceSectionTitle: 'Resposta por voz',
  voiceResponseLabel: 'Responder por áudio',
  voiceResponseDescription:
    'Quando o cliente mandar um áudio, o agente também responde em áudio.',
  voiceResponseVoiceLabel: 'Voz',
  voiceResponseModelLabel: 'Modelo de voz',
  voiceResponseModelAutomaticHint: AUTOMATIC_MODEL_HINT,
  voiceResponseProfileLabel: 'Perfil de fala',
  voiceResponseProfileIneffectiveWarning:
    'Este perfil não tem efeito no modelo TTS-1 (não suporta instruções de fala). Troque o modelo de voz ou selecione "Neutro".',
  audioInputUnsupportedNotice:
    'Este canal ainda não transcreve mensagens de voz recebidas — a IA vai responder apenas ao texto da conversa.',
  audioOutputUnsupportedNotice:
    'Este canal ainda não permite responder com áudio.',
  audioOutputIncompatibleWarning:
    'A resposta por áudio está salva como ativa, mas este canal não suporta mais o envio de áudio. Desligue a opção ou ela continuará sem efeito.',
  submitLabel: 'Salvar agente',
  savingLabel: 'Salvando...',
  editLabel: 'Editar configuração',
  cancelLabel: 'Cancelar',
  activeLabel: 'Ativo',
  inactiveLabel: 'Inativo',
  statusLabel: 'Status',
  providerSummaryLabel: 'Provedor',
  toneSummaryLabel: 'Tom de atendimento',
  successMessage: 'Agente salvo com sucesso.',
  noInboxHelp: 'Selecione uma caixa de entrada antes de salvar o agente IA.',
  noProviderHelp:
    'Conecte um provedor de IA antes de ativar o atendimento automático.',
  blockedByPlanHelp:
    'Este canal não está disponível no plano atual. A resposta automática desta caixa foi desativada porque o canal não está disponível no plano atual.',
  fallbackError: 'Não foi possível salvar o agente.',
};

const agentForm = ref({
  inbox_id: '',
  name: 'Assistente IA Start',
  enabled: false,
  ai_provider_credential_id: '',
  tone: 'profissional, claro e acolhedor',
  instructions:
    'Responder dúvidas simples, pedir contexto quando necessário e chamar humano em casos sensíveis.',
  greeting_message: 'Olá, sou o assistente virtual do atendimento.',
  temperature: 0.4,
  max_tokens: 700,
  voice_response_enabled: false,
  voice_response_voice: 'alloy',
  voice_response_model: NEW_AGENT_SPEECH_MODEL,
  voice_response_profile: NEW_AGENT_SPEECH_PROFILE,
});

// Tracks which tone option is shown as selected: either a known preset's own
// value, or the "Personalizado" sentinel when the persisted tone doesn't
// match any preset exactly (including a brand-new, never-saved agent).
const toneSelection = ref(CUSTOM_TONE_VALUE);

const isSavingAgent = ref(false);
const isEditingAgent = ref(!props.agent?.id);

const DEFAULT_VOICE_OPTIONS = [
  'alloy',
  'echo',
  'fable',
  'onyx',
  'nova',
  'shimmer',
];

const voiceOptions = computed(() => {
  const voices = props.agent?.available_voices?.length
    ? props.agent.available_voices
    : DEFAULT_VOICE_OPTIONS;

  return voices.map(voice => ({ label: voice, value: voice }));
});

const tonePresetOptions = computed(() => {
  return [
    ...TONE_PRESETS.map(preset => ({
      label: preset.label,
      value: preset.value,
    })),
    { label: agentFormContent.toneCustomOptionLabel, value: CUSTOM_TONE_VALUE },
  ];
});

const isCustomToneSelected = computed(() => {
  return toneSelection.value === CUSTOM_TONE_VALUE;
});

const speechModelOptions = computed(() => SPEECH_MODEL_OPTIONS);
const speechProfileOptions = computed(() => SPEECH_PROFILE_OPTIONS);

const isAutomaticSpeechModelSelected = computed(() => {
  return agentForm.value.voice_response_model === 'automatic';
});

const currentSpeechModelSupportsProfile = computed(() => {
  return !SPEECH_MODELS_WITHOUT_PROFILE_SUPPORT.includes(
    agentForm.value.voice_response_model
  );
});

// Only true while the persisted/selected combination actually has no effect -
// never mutates data on its own, just informs the user before they change
// something. Covers a legacy record loaded as-is just as much as a model the
// user is actively changing.
const showsIneffectiveProfileWarning = computed(() => {
  return (
    !currentSpeechModelSupportsProfile.value &&
    agentForm.value.voice_response_profile === 'pt_br_neutral'
  );
});

// Only fires on a direct user interaction with the model select (never during
// applyAgentDefaults), so an existing agent stored in an unusual combination
// keeps its data untouched until the user actively changes something and saves.
const handleSpeechModelChange = value => {
  agentForm.value.voice_response_model = value;

  if (
    SPEECH_MODELS_WITHOUT_PROFILE_SUPPORT.includes(value) &&
    agentForm.value.voice_response_profile === 'pt_br_neutral'
  ) {
    agentForm.value.voice_response_profile = 'neutral';
  }
};

const handleToneSelectionChange = value => {
  toneSelection.value = value;

  if (value !== CUSTOM_TONE_VALUE) {
    agentForm.value.tone = value;
  }
};

const providerCredentialOptions = computed(() => {
  return props.providerCredentials.map(credential => ({
    label: `${formatProviderName(credential.provider)} · ${
      credential.default_model || credential.masked_api_key
    }`,
    value: String(credential.id),
  }));
});

const hasInbox = computed(() => {
  return Boolean(props.inbox?.id);
});

const isBlockedByPlan = computed(() => {
  return Boolean(props.inbox && props.inbox.ai_allowed === false);
});

// Backend is the sole authority on channel media capability (see
// Kraftchat::Ai::ChannelCapabilities) - this form never re-derives which
// channels support audio, it only reads whatever the API already resolved
// for this agent (when editing) or this inbox (when configuring a new one).
const capabilities = computed(() => {
  return (
    props.agent?.capabilities ||
    props.inbox?.capabilities || {
      audio_input: false,
      audio_output: false,
    }
  );
});

const audioInputSupported = computed(() => {
  return Boolean(capabilities.value.audio_input);
});

const audioOutputSupported = computed(() => {
  return Boolean(capabilities.value.audio_output);
});

// Never block turning voice OFF - only prevent turning it ON for a channel
// that can't deliver audio. An agent saved before the channel became
// incompatible keeps its stored value untouched here.
const voiceSwitchDisabled = computed(() => {
  return !audioOutputSupported.value && !agentForm.value.voice_response_enabled;
});

const showVoiceIncompatibleWarning = computed(() => {
  return !audioOutputSupported.value && agentForm.value.voice_response_enabled;
});

const agentSubmitLabel = computed(() => {
  return isSavingAgent.value
    ? agentFormContent.savingLabel
    : agentFormContent.submitLabel;
});

const canSaveAgent = computed(() => {
  return Boolean(
    agentForm.value.inbox_id &&
      agentForm.value.ai_provider_credential_id &&
      props.providerCredentials.length &&
      !isBlockedByPlan.value
  );
});

const selectedProviderLabel = computed(() => {
  const credentialId = agentForm.value.ai_provider_credential_id;
  const credential = props.providerCredentials.find(
    provider => String(provider.id) === String(credentialId)
  );

  if (!credential) {
    return '-';
  }

  return `${formatProviderName(credential.provider)} · ${
    credential.default_model || credential.masked_api_key
  }`;
});

const selectedInboxLabel = computed(() => {
  return props.inbox?.name || '-';
});

const selectedChannelLabel = computed(() => {
  return props.inbox?.channel_type
    ? formatChannelType(props.inbox.channel_type)
    : '-';
});

const agentStatusLabel = computed(() => {
  return agentForm.value.enabled
    ? agentFormContent.activeLabel
    : agentFormContent.inactiveLabel;
});

const agentEnabledLabel = computed(() => {
  return agentForm.value.enabled
    ? agentFormContent.enabledActiveLabel
    : agentFormContent.enabledInactiveLabel;
});

const hasPendingEnabledChange = computed(() => {
  return agentForm.value.enabled !== Boolean(props.agent?.enabled);
});

const applyAgentDefaults = () => {
  const fallbackCredential = props.providerCredentials[0];

  agentForm.value = {
    inbox_id: String(props.agent?.inbox_id || props.inbox?.id || ''),
    name: props.agent?.name || 'Assistente IA Start',
    enabled: Boolean(props.agent?.enabled),
    ai_provider_credential_id: String(
      props.agent?.ai_provider_credential_id || fallbackCredential?.id || ''
    ),
    tone: props.agent?.tone || 'profissional, claro e acolhedor',
    instructions:
      props.agent?.instructions ||
      'Responder dúvidas simples, pedir contexto quando necessário e chamar humano em casos sensíveis.',
    greeting_message:
      props.agent?.greeting_message ||
      'Olá, sou o assistente virtual do atendimento.',
    temperature: Number(props.agent?.temperature || 0.4),
    max_tokens: Number(props.agent?.max_tokens || 700),
    voice_response_enabled: Boolean(props.agent?.voice_response_enabled),
    voice_response_voice: props.agent?.voice_response_voice || 'alloy',
    // Existing agents always carry their own persisted values here (the
    // backend defaults new columns to tts-1/neutral for backward
    // compatibility) - these fallbacks only apply to a brand-new,
    // never-saved agent, where NEW_AGENT_SPEECH_MODEL/PROFILE are the
    // deliberately different UI defaults.
    voice_response_model:
      props.agent?.voice_response_model || NEW_AGENT_SPEECH_MODEL,
    voice_response_profile:
      props.agent?.voice_response_profile || NEW_AGENT_SPEECH_PROFILE,
  };

  toneSelection.value = resolveTonePresetValue(agentForm.value.tone);

  if (!props.agent?.id) {
    isEditingAgent.value = true;
  }
};

const handleSaveAgent = async () => {
  if (!agentForm.value.inbox_id) {
    useAlert(agentFormContent.noInboxHelp);
    return;
  }

  if (!agentForm.value.ai_provider_credential_id) {
    useAlert(agentFormContent.noProviderHelp);
    return;
  }

  isSavingAgent.value = true;

  const payload = {
    inbox_id: Number(agentForm.value.inbox_id),
    ai_provider_credential_id: Number(
      agentForm.value.ai_provider_credential_id
    ),
    name: agentForm.value.name,
    enabled: agentForm.value.enabled,
    tone: agentForm.value.tone,
    instructions: agentForm.value.instructions,
    greeting_message: agentForm.value.greeting_message,
    temperature: Number(agentForm.value.temperature),
    max_tokens: Number(agentForm.value.max_tokens),
    voice_response_enabled: agentForm.value.voice_response_enabled,
    voice_response_voice: agentForm.value.voice_response_voice,
    voice_response_model: agentForm.value.voice_response_model,
    voice_response_profile: agentForm.value.voice_response_profile,
  };

  try {
    if (props.agent?.id) {
      await KraftchatAIAPI.updateAgent(props.agent.id, payload);
    } else {
      await KraftchatAIAPI.createAgent(payload);
    }

    useAlert(agentFormContent.successMessage);
    isEditingAgent.value = false;
    emit('saved');
  } catch (error) {
    useAlert(
      error.response?.data?.message ||
        error.message ||
        agentFormContent.fallbackError
    );
  } finally {
    isSavingAgent.value = false;
  }
};

watch(
  () => [props.agent, props.providerCredentials, props.inbox],
  () => {
    applyAgentDefaults();
  },
  { immediate: true }
);
</script>

<template>
  <section class="rounded-xl border border-n-weak bg-n-solid-1 p-6 shadow-sm">
    <div class="max-w-5xl space-y-5">
      <div class="space-y-1">
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ agentFormContent.title }}
        </h2>

        <p class="text-sm leading-6 text-n-slate-11">
          {{ agentFormContent.description }}
        </p>
      </div>

      <div
        v-if="!isEditingAgent"
        class="space-y-4 rounded-xl border border-n-weak bg-n-background p-4"
      >
        <div class="grid gap-4 md:grid-cols-4">
          <div>
            <p
              class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
            >
              {{ agentFormContent.statusLabel }}
            </p>

            <p class="mt-1 text-sm font-semibold text-n-slate-12">
              {{ agentStatusLabel }}
            </p>
          </div>

          <div>
            <p
              class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
            >
              {{ agentFormContent.inboxSummaryLabel }}
            </p>

            <p class="mt-1 text-sm font-semibold text-n-slate-12">
              {{ selectedInboxLabel }}
              <span class="font-normal text-n-slate-10"
                >· {{ selectedChannelLabel }}</span
              >
            </p>
          </div>

          <div>
            <p
              class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
            >
              {{ agentFormContent.providerSummaryLabel }}
            </p>

            <p class="mt-1 text-sm font-semibold text-n-slate-12">
              {{ selectedProviderLabel }}
            </p>
          </div>

          <div>
            <p
              class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
            >
              {{ agentFormContent.toneSummaryLabel }}
            </p>

            <p class="mt-1 truncate text-sm font-semibold text-n-slate-12">
              {{ agentForm.tone }}
            </p>
          </div>
        </div>

        <NextButton
          type="button"
          outline
          slate
          :label="agentFormContent.editLabel"
          @click="isEditingAgent = true"
        />
      </div>

      <div v-else class="space-y-5">
        <div
          class="flex items-center justify-between gap-4 rounded-xl border border-n-weak bg-n-background p-4"
        >
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <p class="text-sm font-medium text-n-slate-12">
                {{ agentFormContent.enabledLabel }}
              </p>

              <span
                class="rounded-md px-2 py-0.5 text-xs font-medium"
                :class="
                  agentForm.enabled
                    ? 'bg-n-brand/10 text-n-brand'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                {{ agentEnabledLabel }}
              </span>
            </div>

            <p class="mt-1 text-sm leading-6 text-n-slate-11">
              {{ agentFormContent.enabledDescription }}
            </p>

            <p
              v-if="hasPendingEnabledChange"
              class="mt-1 text-xs font-medium text-n-amber-11"
            >
              {{ agentFormContent.pendingChangeLabel }}
            </p>
          </div>

          <Switch
            v-model="agentForm.enabled"
            :disabled="
              !providerCredentials.length || !hasInbox || isBlockedByPlan
            "
          />
        </div>

        <p
          v-if="!providerCredentials.length"
          class="text-sm font-medium text-n-ruby-11"
        >
          {{ agentFormContent.noProviderHelp }}
        </p>

        <p
          v-if="!hasInbox"
          class="rounded-lg border border-n-weak bg-n-background p-3 text-sm font-medium text-n-ruby-11"
        >
          {{ agentFormContent.noInboxHelp }}
        </p>

        <p
          v-if="isBlockedByPlan"
          class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 p-3 text-sm font-medium text-n-ruby-11"
        >
          {{ agentFormContent.blockedByPlanHelp }}
        </p>

        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ agentFormContent.identitySectionTitle }}
        </h3>

        <div class="grid gap-4 md:grid-cols-2">
          <Input
            v-model="agentForm.name"
            :label="agentFormContent.nameLabel"
            size="md"
            type="text"
          />

          <div class="flex flex-col gap-1">
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ agentFormContent.providerLabel }}
            </label>

            <SelectMenu
              v-if="providerCredentialOptions.length"
              v-model="agentForm.ai_provider_credential_id"
              class="!w-full [&>button]:!h-10 [&>button]:!w-full [&>button]:!max-w-none [&>button]:!justify-between [&>button]:!px-3 [&>div]:!w-full [&>div]:!max-w-none"
              :label="selectedProviderLabel"
              :options="providerCredentialOptions"
              sub-menu-position="bottom"
            />

            <div
              v-else
              class="flex h-10 w-full items-center rounded-lg bg-n-alpha-black2 px-3 text-sm text-n-slate-11 outline outline-1 outline-offset-[-1px] outline-n-weak"
            >
              {{ agentFormContent.noProviderHelp }}
            </div>
          </div>

          <div v-if="hasInbox" class="flex flex-col justify-end gap-1">
            <label class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ agentFormContent.inboxLabel }}
            </label>

            <div
              class="rounded-lg border border-n-weak bg-n-background px-3 py-2"
            >
              <p class="text-sm font-medium text-n-slate-12">
                {{ selectedInboxLabel }}
              </p>

              <p class="mt-1 text-xs text-n-slate-11">
                {{ selectedChannelLabel }}
              </p>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ agentFormContent.toneLabel }}
          </label>

          <SelectMenu
            v-model="toneSelection"
            class="!w-full [&>button]:!h-10 [&>button]:!w-full [&>button]:!max-w-none [&>button]:!justify-between [&>button]:!px-3 [&>div]:!w-full [&>div]:!max-w-none"
            :label="
              tonePresetOptions.find(option => option.value === toneSelection)
                ?.label
            "
            :options="tonePresetOptions"
            sub-menu-position="bottom"
            @update:model-value="handleToneSelectionChange"
          />

          <Input
            v-if="isCustomToneSelected"
            v-model="agentForm.tone"
            class="mt-2"
            size="md"
            type="text"
          />
        </div>

        <TextArea
          v-model="agentForm.instructions"
          :label="agentFormContent.instructionsLabel"
          :max-length="1000"
          show-character-count
          min-height="7rem"
          resize
        />

        <TextArea
          v-model="agentForm.greeting_message"
          :label="agentFormContent.greetingMessageLabel"
          :max-length="300"
          show-character-count
          min-height="5rem"
          resize
        />

        <div
          class="space-y-4 rounded-xl border border-n-weak bg-n-background p-4"
        >
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ agentFormContent.voiceSectionTitle }}
          </h3>

          <p
            v-if="!audioInputSupported"
            class="rounded-lg border border-n-amber-6 bg-n-amber-2 p-3 text-sm font-medium text-n-amber-11"
          >
            {{ agentFormContent.audioInputUnsupportedNotice }}
          </p>

          <div class="flex h-10 items-center gap-2">
            <Switch
              v-model="agentForm.voice_response_enabled"
              :disabled="voiceSwitchDisabled"
            />
            <span class="text-sm text-n-slate-11">
              {{ agentFormContent.voiceResponseDescription }}
            </span>
          </div>

          <p
            v-if="showVoiceIncompatibleWarning"
            class="rounded-lg border border-n-ruby-6 bg-n-ruby-2 p-3 text-sm font-medium text-n-ruby-11"
          >
            {{ agentFormContent.audioOutputIncompatibleWarning }}
          </p>

          <p v-else-if="!audioOutputSupported" class="text-xs text-n-slate-11">
            {{ agentFormContent.audioOutputUnsupportedNotice }}
          </p>

          <div
            v-if="agentForm.voice_response_enabled"
            class="grid gap-4 md:grid-cols-3"
          >
            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{ agentFormContent.voiceResponseModelLabel }}
              </label>

              <SelectMenu
                :model-value="agentForm.voice_response_model"
                class="!w-full [&>button]:!h-10 [&>button]:!w-full [&>button]:!max-w-none [&>button]:!justify-between [&>button]:!px-3 [&>div]:!w-full [&>div]:!max-w-none"
                :label="
                  speechModelOptions.find(
                    option => option.value === agentForm.voice_response_model
                  )?.label
                "
                :options="speechModelOptions"
                sub-menu-position="bottom"
                @update:model-value="handleSpeechModelChange"
              />

              <p
                v-if="isAutomaticSpeechModelSelected"
                class="text-xs text-n-slate-11"
              >
                {{ agentFormContent.voiceResponseModelAutomaticHint }}
              </p>
            </div>

            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{ agentFormContent.voiceResponseVoiceLabel }}
              </label>

              <SelectMenu
                v-model="agentForm.voice_response_voice"
                class="!w-full [&>button]:!h-10 [&>button]:!w-full [&>button]:!max-w-none [&>button]:!justify-between [&>button]:!px-3 [&>div]:!w-full [&>div]:!max-w-none"
                :label="agentForm.voice_response_voice"
                :options="voiceOptions"
                sub-menu-position="bottom"
              />
            </div>

            <div class="flex flex-col gap-1">
              <label class="mb-0.5 text-heading-3 text-n-slate-12">
                {{ agentFormContent.voiceResponseProfileLabel }}
              </label>

              <SelectMenu
                v-model="agentForm.voice_response_profile"
                class="!w-full [&>button]:!h-10 [&>button]:!w-full [&>button]:!max-w-none [&>button]:!justify-between [&>button]:!px-3 [&>div]:!w-full [&>div]:!max-w-none"
                :label="
                  speechProfileOptions.find(
                    option => option.value === agentForm.voice_response_profile
                  )?.label
                "
                :options="speechProfileOptions"
                sub-menu-position="bottom"
              />

              <p
                v-if="showsIneffectiveProfileWarning"
                class="text-xs text-n-amber-11"
              >
                {{ agentFormContent.voiceResponseProfileIneffectiveWarning }}
              </p>
            </div>
          </div>
        </div>

        <Accordion :title="agentFormContent.advancedSectionTitle">
          <div class="grid gap-4 md:grid-cols-2">
            <div class="flex flex-col gap-1">
              <Input
                v-model.number="agentForm.temperature"
                :label="agentFormContent.temperatureLabel"
                max="2"
                min="0"
                size="md"
                step="0.1"
                type="number"
              />
              <p class="text-xs text-n-slate-11">
                {{ agentFormContent.temperatureHelp }}
              </p>
            </div>

            <div class="flex flex-col gap-1">
              <Input
                v-model.number="agentForm.max_tokens"
                :label="agentFormContent.maxTokensLabel"
                max="4000"
                min="1"
                size="md"
                type="number"
              />
              <p class="text-xs text-n-slate-11">
                {{ agentFormContent.maxTokensHelp }}
              </p>
            </div>
          </div>
        </Accordion>

        <NextButton
          type="button"
          :disabled="!canSaveAgent"
          :is-loading="isSavingAgent"
          :label="agentSubmitLabel"
          @click="handleSaveAgent"
        />
      </div>
    </div>
  </section>
</template>
